import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  getLegalEntityCounterparty,
  getPhysicalEntityCounterparty,
  getCounterpartyBankAccounts,
} from "../../../../api/api";
import "./CounterpartyDetails.css";

const CounterpartyDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const tabType = searchParams.get("type") || "counterparty";
  const entityKind = searchParams.get("entity") || "legal_entity";
  const [counterparty, setCounterparty] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    basicInfo: true,
    addresses: true,
    management: true,
    contacts: true,
    licenses: true,
    taxes: true,
    bankAccounts: true,
  });

  /** Приводит ответ API контрагентов к единому формату для отображения */
  const normalizeCounterpartyFromApi = (data) => {
    if (!data) return null;
    const parts = [
      data.country,
      data.postal_code,
      data.city,
      data.street,
      data.building,
    ].filter(Boolean);
    const legalAddressStr = parts.length > 0 ? parts.join(", ") : null;
    return {
      ...data,
      bin_iin: data.bin_or_inn ?? data.bin_iin,
      short_name: data.short_company_name ?? data.short_name ?? data.full_name,
      full_name: data.full_company_name ?? data.full_name,
      director_full_name:
        data.full_name_first_supervisor ?? data.director_full_name,
      contact_person_email: data.email ?? data.contact_person_email,
      legal_address: legalAddressStr,
      actual_address: data.is_actual_equals_legal ? legalAddressStr : null,
    };
  };

  useEffect(() => {
    const loadCounterparty = async () => {
      if (!id) {
        setError("ID контрагента не указан");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const fetchData =
          entityKind === "physical_entity"
            ? getPhysicalEntityCounterparty(id)
            : getLegalEntityCounterparty(id);
        const data = await fetchData;
        setCounterparty(normalizeCounterpartyFromApi(data));

        // Банковские счета: GET /partners/counterparties/bank_account/all/{counterparty_id}
        const counterpartyId = parseInt(id, 10);
        if (!Number.isNaN(counterpartyId)) {
          try {
            const accounts = await getCounterpartyBankAccounts(counterpartyId);
            setBankAccounts(Array.isArray(accounts) ? accounts : []);
          } catch (bankErr) {
            console.error("Ошибка при загрузке банковских счетов:", bankErr);
            setBankAccounts([]);
          }
        } else {
          setBankAccounts([]);
        }
      } catch (err) {
        console.error("Ошибка при загрузке контрагента:", err);
        setError("Не удалось загрузить данные контрагента");
      } finally {
        setLoading(false);
      }
    };

    loadCounterparty();
  }, [id, entityKind]);

  const toggleSection = (sectionName) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionName]: !prev[sectionName],
    }));
  };

  const parseAddress = (addressStr) => {
    if (!addressStr) return null;
    const parts = addressStr.split(",").map((p) => p.trim());
    return {
      country: parts[0] || "",
      postalCode: parts[1] || "",
      city: parts[2] || "",
      street: parts.slice(3).join(", ") || "",
    };
  };

  if (loading) {
    return (
      <div className="counterparty-details-container">
        <div className="counterparty-details-loading">Загрузка данных...</div>
      </div>
    );
  }

  if (error || !counterparty) {
    return (
      <div className="counterparty-details-container">
        <div className="counterparty-details-error">
          {error || "Контрагент не найден"}
        </div>
        <button
          className="counterparty-details-back-btn"
          onClick={() => navigate("/projects/counterparties")}
        >
          Вернуться к списку
        </button>
      </div>
    );
  }

  const legalAddress = parseAddress(counterparty.legal_address);
  const actualAddress = parseAddress(counterparty.actual_address);

  return (
    <div className="counterparty-details-container">
      <div className="counterparty-details-header">
        <button
          className="counterparty-details-back-button"
          onClick={() => navigate("/projects/counterparties")}
        >
          ← Назад к списку
        </button>
        <h1 className="counterparty-details-title">Детали контрагента</h1>
      </div>

      <div className="counterparty-details-name-section">
        <h2 className="counterparty-details-name">
          {counterparty.short_name || counterparty.full_name || "—"}
        </h2>
        <p className="counterparty-details-bin">
          БИН {counterparty.bin_iin || "—"}
        </p>
      </div>

      {/* Основная информация */}
      <div className="counterparty-details-section">
        <div
          className="counterparty-details-section-header"
          onClick={() => toggleSection("basicInfo")}
        >
          <h3 className="counterparty-details-section-title">
            Основная информация
          </h3>
          <span
            className={`counterparty-details-section-arrow ${
              expandedSections.basicInfo ? "expanded" : ""
            }`}
          >
            {expandedSections.basicInfo ? "↓" : "↑"}
          </span>
        </div>
        {expandedSections.basicInfo && (
          <div className="counterparty-details-section-content">
            <div className="counterparty-details-field">
              <span className="counterparty-details-field-label">
                Организационно-правовая форма:
              </span>
              <span className="counterparty-details-field-value">
                {counterparty.legal_form || "—"}
              </span>
            </div>
            <div className="counterparty-details-field">
              <span className="counterparty-details-field-label">
                БИН (ИИН):
              </span>
              <span className="counterparty-details-field-value">
                {counterparty.bin_iin || "—"}
              </span>
            </div>
            <div className="counterparty-details-field">
              <span className="counterparty-details-field-label">
                Полное наименование:
              </span>
              <span className="counterparty-details-field-value">
                {counterparty.full_name || "—"}
              </span>
            </div>
            <div className="counterparty-details-field">
              <span className="counterparty-details-field-label">
                Сокращенное наименование:
              </span>
              <span className="counterparty-details-field-value">
                {counterparty.short_name || "—"}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Адреса */}
      <div className="counterparty-details-section">
        <div
          className="counterparty-details-section-header"
          onClick={() => toggleSection("addresses")}
        >
          <h3 className="counterparty-details-section-title">Адреса</h3>
          <span
            className={`counterparty-details-section-arrow ${
              expandedSections.addresses ? "expanded" : ""
            }`}
          >
            {expandedSections.addresses ? "↓" : "↑"}
          </span>
        </div>
        {expandedSections.addresses && (
          <div className="counterparty-details-section-content">
            <div className="counterparty-details-address-block">
              <h4 className="counterparty-details-address-title">
                ЮРИДИЧЕСКИЙ АДРЕС
              </h4>
              {legalAddress ? (
                <>
                  <div className="counterparty-details-field">
                    <span className="counterparty-details-field-label">
                      Страна:
                    </span>
                    <span className="counterparty-details-field-value">
                      {legalAddress.country || "—"}
                    </span>
                  </div>
                  <div className="counterparty-details-field">
                    <span className="counterparty-details-field-label">
                      Индекс:
                    </span>
                    <span className="counterparty-details-field-value">
                      {legalAddress.postalCode || "—"}
                    </span>
                  </div>
                  <div className="counterparty-details-field">
                    <span className="counterparty-details-field-label">
                      Город / населённый пункт:
                    </span>
                    <span className="counterparty-details-field-value">
                      {legalAddress.city || "—"}
                    </span>
                  </div>
                  <div className="counterparty-details-field">
                    <span className="counterparty-details-field-label">
                      Улица, дом, офис:
                    </span>
                    <span className="counterparty-details-field-value">
                      {legalAddress.street || "—"}
                    </span>
                  </div>
                </>
              ) : (
                <div className="counterparty-details-field">
                  <span className="counterparty-details-field-value">—</span>
                </div>
              )}
            </div>

            <div className="counterparty-details-address-block">
              <h4 className="counterparty-details-address-title">
                ФАКТИЧЕСКИЙ АДРЕС
              </h4>
              {actualAddress ? (
                <>
                  <div className="counterparty-details-field">
                    <span className="counterparty-details-field-label">
                      Страна:
                    </span>
                    <span className="counterparty-details-field-value">
                      {actualAddress.country || "—"}
                    </span>
                  </div>
                  <div className="counterparty-details-field">
                    <span className="counterparty-details-field-label">
                      Индекс:
                    </span>
                    <span className="counterparty-details-field-value">
                      {actualAddress.postalCode || "—"}
                    </span>
                  </div>
                  <div className="counterparty-details-field">
                    <span className="counterparty-details-field-label">
                      Город / населённый пункт:
                    </span>
                    <span className="counterparty-details-field-value">
                      {actualAddress.city || "—"}
                    </span>
                  </div>
                  <div className="counterparty-details-field">
                    <span className="counterparty-details-field-label">
                      Улица, номер здания:
                    </span>
                    <span className="counterparty-details-field-value">
                      {actualAddress.street || "—"}
                    </span>
                  </div>
                </>
              ) : (
                <div className="counterparty-details-field">
                  <span className="counterparty-details-field-value">—</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Руководство и общие контакты */}
      <div className="counterparty-details-section">
        <div
          className="counterparty-details-section-header"
          onClick={() => toggleSection("management")}
        >
          <h3 className="counterparty-details-section-title">
            Руководство и общие контакты
          </h3>
          <span
            className={`counterparty-details-section-arrow ${
              expandedSections.management ? "expanded" : ""
            }`}
          >
            {expandedSections.management ? "↓" : "↑"}
          </span>
        </div>
        {expandedSections.management && (
          <div className="counterparty-details-section-content">
            <div className="counterparty-details-field">
              <span className="counterparty-details-field-label">
                ФИО первого руководителя:
              </span>
              <span className="counterparty-details-field-value">
                {counterparty.director_full_name || "—"}
              </span>
            </div>
            <div className="counterparty-details-field">
              <span className="counterparty-details-field-label">
                Телефон организации:
              </span>
              <span className="counterparty-details-field-value">
                {counterparty.phone || "—"}
              </span>
            </div>
            <div className="counterparty-details-field">
              <span className="counterparty-details-field-label">
                E-mail организации:
              </span>
              <span className="counterparty-details-field-value">
                {counterparty.contact_person_email || "—"}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Контактные лица */}
      <div className="counterparty-details-section">
        <div
          className="counterparty-details-section-header"
          onClick={() => toggleSection("contacts")}
        >
          <h3 className="counterparty-details-section-title">
            Контактные лица
          </h3>
          <span
            className={`counterparty-details-section-arrow ${
              expandedSections.contacts ? "expanded" : ""
            }`}
          >
            {expandedSections.contacts ? "↓" : "↑"}
          </span>
        </div>
        {expandedSections.contacts && (
          <div className="counterparty-details-section-content">
            {counterparty.contact_person ? (
              <table className="counterparty-details-table">
                <thead>
                  <tr>
                    <th>ФИО</th>
                    <th>Роль</th>
                    <th>Телефон</th>
                    <th>E-mail</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{counterparty.contact_person || "—"}</td>
                    <td>—</td>
                    <td>{counterparty.contact_person_phone || "—"}</td>
                    <td>{counterparty.contact_person_email || "—"}</td>
                  </tr>
                </tbody>
              </table>
            ) : (
              <div className="counterparty-details-empty">
                Контактные лица не указаны
              </div>
            )}
          </div>
        )}
      </div>

      {/* Лицензии */}
      <div className="counterparty-details-section">
        <div
          className="counterparty-details-section-header"
          onClick={() => toggleSection("licenses")}
        >
          <h3 className="counterparty-details-section-title">Лицензии</h3>
          <span
            className={`counterparty-details-section-arrow ${
              expandedSections.licenses ? "expanded" : ""
            }`}
          >
            {expandedSections.licenses ? "↓" : "↑"}
          </span>
        </div>
        {expandedSections.licenses && (
          <div className="counterparty-details-section-content">
            {counterparty.license_number ? (
              <table className="counterparty-details-table">
                <thead>
                  <tr>
                    <th>Номер лицензии</th>
                    <th>Дата выдачи</th>
                    <th>Срок действия</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{counterparty.license_number || "—"}</td>
                    <td>
                      {counterparty.license_issue_date
                        ? new Date(
                            counterparty.license_issue_date,
                          ).toLocaleDateString("ru-RU")
                        : "—"}
                    </td>
                    <td>
                      {counterparty.license_expiration_date
                        ? new Date(
                            counterparty.license_expiration_date,
                          ).toLocaleDateString("ru-RU")
                        : "—"}
                    </td>
                  </tr>
                </tbody>
              </table>
            ) : (
              <div className="counterparty-details-empty">
                Лицензии не указаны
              </div>
            )}
          </div>
        )}
      </div>

      {/* Налоги */}
      <div className="counterparty-details-section">
        <div
          className="counterparty-details-section-header"
          onClick={() => toggleSection("taxes")}
        >
          <h3 className="counterparty-details-section-title">Налоги</h3>
          <span
            className={`counterparty-details-section-arrow ${
              expandedSections.taxes ? "expanded" : ""
            }`}
          >
            {expandedSections.taxes ? "↓" : "↑"}
          </span>
        </div>
        {expandedSections.taxes && (
          <div className="counterparty-details-section-content">
            <div className="counterparty-details-field">
              <span className="counterparty-details-field-label">
                Режим налогообложения:
              </span>
              <span className="counterparty-details-field-value">
                {counterparty.tax_regime || "—"}
              </span>
            </div>
            <div className="counterparty-details-field">
              <span className="counterparty-details-field-label">
                Плательщик НДС:
              </span>
              <span className="counterparty-details-field-value">
                {counterparty.is_nds_payer ? "Да" : "Нет"}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Банковские счета */}
      <div className="counterparty-details-section">
        <div
          className="counterparty-details-section-header"
          onClick={() => toggleSection("bankAccounts")}
        >
          <h3 className="counterparty-details-section-title">
            Банковские счета
          </h3>
          <span
            className={`counterparty-details-section-arrow ${
              expandedSections.bankAccounts ? "expanded" : ""
            }`}
          >
            {expandedSections.bankAccounts ? "↓" : "↑"}
          </span>
        </div>
        {expandedSections.bankAccounts && (
          <div className="counterparty-details-section-content">
            {bankAccounts.length > 0 ? (
              <table className="counterparty-details-table">
                <thead>
                  <tr>
                    <th>Банк</th>
                    <th>БИК</th>
                    <th>ИИК / № счёта</th>
                    <th>Валюта</th>
                    <th>Основной</th>
                  </tr>
                </thead>
                <tbody>
                  {bankAccounts.map((acc) => (
                    <tr key={acc.id}>
                      <td>{acc.bank_name || "—"}</td>
                      <td>{acc.bik || "—"}</td>
                      <td>{acc.iik || "—"}</td>
                      <td>{acc.currency || "—"}</td>
                      <td>{acc.is_primary ? "Да" : "Нет"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="counterparty-details-empty">
                Банковские счета не указаны
              </div>
            )}
          </div>
        )}
      </div>

      {/* Кнопка редактирования */}
      <div className="counterparty-details-actions">
        <button
          className="counterparty-details-edit-btn"
          onClick={() =>
            navigate(
              `/projects/counterparties/edit/${id}?type=${tabType}&entity=${entityKind}`,
            )
          }
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M11.3333 2.00001C11.5084 1.82489 11.7163 1.68697 11.9439 1.59531C12.1715 1.50365 12.4138 1.46021 12.6578 1.46771C12.9017 1.47521 13.1419 1.53351 13.3627 1.63889C13.5835 1.74427 13.7799 1.89428 13.9393 2.07964C14.0988 2.265 14.2178 2.48134 14.2889 2.71428C14.36 2.94722 14.3813 3.19182 14.3513 3.43246C14.3214 3.6731 14.2409 3.90458 14.115 4.11112C13.9891 4.31765 13.8207 4.49474 13.6213 4.63068L6.62133 11.6307L2.66667 12.6667L3.70267 8.71201L10.7027 1.71201L11.3333 2.00001Z"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Редактировать
        </button>
      </div>
    </div>
  );
};

export default CounterpartyDetails;
